// Utility functions for the application

// Format currency
export const formatCurrency = (amount, currency = 'USD') => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

// Format date
export const formatDate = (date, options = {}) => {
  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  };

  return new Intl.DateTimeFormat('en-US', { ...defaultOptions, ...options }).format(new Date(date));
};

// Format number with commas
export const formatNumber = (number) => {
  return new Intl.NumberFormat('en-US').format(number);
};

// Debounce function
export const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Validate email
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Generate unique ID
export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Storage utilities (using localStorage for persistent sessions)
export const storage = {
  get: (key) => {
    try {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.error('Error getting from localStorage:', error);
      return null;
    }
  },

  set: (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error setting localStorage:', error);
    }
  },

  remove: (key) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error removing from localStorage:', error);
    }
  },
};

// Backend error message mapping utility
export const getErrorMessage = (error, fallbackMessage = 'An error occurred') => {
  if (!error) return fallbackMessage;

  // Handle error objects with response data
  if (error.response) {
    const { status, data } = error.response;

    // Handle rate limiting errors (429)
    if (status === 429) {
      const retryAfter = error.response.headers?.['retry-after'];
      const retryMessage = retryAfter ? ` Please try again in ${retryAfter} seconds.` : ' Please try again later.';

      if (data?.message) {
        return data.message + retryMessage;
      }
      return 'Too many requests. Please try again later.';
    }

    // Handle authentication errors (401)
    if (status === 401) {
      const message = data?.message;
      if (message) {
        return mapAuthErrorMessage(message);
      }
      return 'Authentication failed. Please check your credentials.';
    }

    // Handle authorization errors (403)
    if (status === 403) {
      const message = data?.message;
      if (message) {
        return mapAuthErrorMessage(message);
      }
      return 'You do not have permission to access this resource.';
    }

    // Handle validation errors (400)
    if (status === 400) {
      // Prefer specific validation details when provided
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        const detailMessage = formatValidationErrorDetail(data.errors[0]);
        return mapValidationErrorMessage(detailMessage || data.message || 'Validation failed');
      }

      const message = data?.message;
      if (message) {
        return mapValidationErrorMessage(message);
      }
      return 'Invalid request. Please check your input.';
    }

    // Handle server errors (500) - Check for database constraint errors
    if (status >= 500) {
      const message = data?.error || data?.message;
      if (message) {
        return mapDatabaseErrorMessage(message);
      }
      return 'Server error. Please try again later.';
    }

    // Use the message from response data if available, check for database errors
    if (data?.message) {
      // If validation errors array exists but status wasn't 400, still try to surface detail
      if (Array.isArray(data?.errors) && data.errors.length > 0) {
        const detailMessage = formatValidationErrorDetail(data.errors[0]);
        return mapValidationErrorMessage(detailMessage || data.message);
      }
      return mapDatabaseErrorMessage(data.message);
    }

    // Check if error field contains database constraint error
    if (data?.error) {
      return mapDatabaseErrorMessage(data.error);
    }
  }

  // Handle error objects with message property - check for database errors
  if (error.message) {
    const mappedMessage = mapDatabaseErrorMessage(error.message) || mapAuthErrorMessage(error.message) || mapValidationErrorMessage(error.message);
    return mappedMessage !== error.message ? mappedMessage : error.message;
  }

  // Handle string errors - check for database errors
  if (typeof error === 'string') {
    const mappedMessage = mapDatabaseErrorMessage(error) || mapAuthErrorMessage(error) || mapValidationErrorMessage(error);
    return mappedMessage !== error ? mappedMessage : error;
  }

  return fallbackMessage;
};

// Convert backend validation detail into user-friendly text
const formatValidationErrorDetail = (detail) => {
  if (!detail) return null;

  // Prefer provided message
  if (detail.message) {
    // Specific handling for machine fields
    if (detail.field === 'machine_name' && detail.message.includes('not allowed to be empty')) {
      return 'Machine name is required.';
    }
    if (detail.field === 'machine_number' && detail.message.includes('not allowed to be empty')) {
      return 'Machine number is required.';
    }
    if (detail.field === 'machine_name' && detail.message.includes('must be a string')) {
      return 'Machine name is required.';
    }
    if (detail.field === 'machine_number' && detail.message.includes('must be a string')) {
      return 'Machine number is required.';
    }
    if (detail.field && /not allowed to be empty|is required/i.test(detail.message)) {
      return buildRequiredFieldMessage(detail.field);
    }
    return detail.message;
  }

  if (detail.field) {
    const readable = buildReadableField(detail.field);
    return `Invalid value for ${readable}`;
  }

  return null;
};

// Map backend database constraint errors to user-friendly messages
const mapDatabaseErrorMessage = (message) => {
  const errorMappings = {
    // Unique constraint violations
    'duplicate key value violates unique constraint': 'This item already exists. Please use a different value.',
    'violates unique constraint': 'This item already exists. Please use a different value.',

    // Foreign key constraint violations
    'violates foreign key constraint': 'Referenced item does not exist or is not available.',
    'insert or update on table violates foreign key constraint': 'Referenced item does not exist or is not available.',

    // Check constraint violations
    'violates check constraint': 'Invalid value provided. Please check your input.',

    // Not null constraint violations
    'violates not-null constraint': 'Required field is missing. Please fill in all required fields.',
    'null value in column violates not-null constraint': 'Required field is missing. Please fill in all required fields.',

    // Generic database errors
    'relation does not exist': 'Database table not found. Please contact support.',
    'column does not exist': 'Database column not found. Please contact support.',
    'permission denied for table': 'Insufficient permissions. Please contact support.',
    'deadlock detected': 'Database conflict occurred. Please try again.',
    'serialization failure': 'Database conflict occurred. Please try again.',
    'could not serialize access': 'Database conflict occurred. Please try again.',

    // Machine-specific errors
    'machines_machine_number_key': 'Machine number already exists. Please use a different machine number.',
    'machines_machine_name_key': 'Machine name already exists. Please use a different machine name.',

    // Supplier-specific errors
    'suppliers_supplier_name_key': 'Supplier name already exists. Please use a different supplier name.',
    'suppliers_phone_number_key': 'Phone number already exists. Please use a different phone number.',
    'suppliers_email_key': 'Email address already exists. Please use a different email address.',

    // Purchase order-specific errors
    'purchase_orders_po_number_key': 'Purchase order number already exists. Please use a unique PO number.',
    'inventory_items_serial_number_key': 'Serial number already exists. Please use a unique serial number.',
  };

  // Check if message contains any of the keys
  for (const [key, friendlyMessage] of Object.entries(errorMappings)) {
    if (message.toLowerCase().includes(key.toLowerCase())) {
      return friendlyMessage;
    }
  }

  return message; // Return original message if no mapping found
};

// Map backend authentication error messages to user-friendly messages
const mapAuthErrorMessage = (message) => {
  const errorMappings = {
    // Login errors
    'Username and password are required': 'Please enter both username and password.',
    'Invalid username or password': 'The username or password you entered is incorrect.',
    'Account is deactivated': 'Your account has been deactivated. Please contact support.',
    'Account is disabled': 'Your account has been disabled. Please contact support.',
    'User not found': 'No account found with these credentials.',

    // Token errors
    'Access token required': 'Please log in to continue.',
    'Token has been revoked': 'Your session has expired. Please log in again.',
    'Invalid or expired token': 'Your session has expired. Please log in again.',
    'Invalid refresh token': 'Your session has expired. Please log in again.',
    'Authentication error': 'Authentication failed. Please log in again.',

    // Rate limiting errors
    'Too many login attempts, please try again later.': 'Too many failed login attempts. Please wait 15 minutes before trying again.',
    'Too many requests from this IP, please try again later.': 'Too many requests. Please wait before trying again.',
    'Too many password reset attempts, please try again later.': 'Too many password reset attempts. Please wait 1 hour before trying again.',
    'Too many data requests, please try again later.': 'Too many requests. Please wait before trying again.',
    'Too many file uploads, please try again later.': 'Too many file uploads. Please wait before trying again.',
    'Too many admin requests, please try again later.': 'Too many admin requests. Please wait before trying again.',

    // Permission errors
    'Permission denied': 'You do not have permission to perform this action.',
    'Tenant access required': 'Access denied. Please contact your administrator.',

    // Refresh token errors
    'Refresh token is required': 'Session expired. Please log in again.',

    // Generic server errors
    'Internal server error': 'Server error. Please try again later.',

    // Network errors
    'Network Error': 'Connection failed. Please check your internet connection.',
    'timeout of 0ms exceeded': 'Request timed out. Please try again.',

    // Default fallback
    'Login failed': 'Login failed. Please check your credentials and try again.',
    'Registration failed': 'Registration failed. Please try again.',
    'Session expired. Please login again.': 'Your session has expired. Please log in again.',
  };

  return errorMappings[message] || message;
};

// Map validation error messages
const mapValidationErrorMessage = (message) => {
  const validationMappings = {
    // Generic validation
    'Validation failed': 'Purchase order validation failed. Please check the highlighted fields.',

    'Username and password are required': 'Please enter both username and password.',
    'Refresh token is required': 'Session expired. Please log in again.',

    // Purchase order item validations
    'description is required': 'Item description is required.',
    'line number must be at least 1': 'Item line number must be at least 1.',
    'unit price must be a number': 'Unit price must be a valid number.',
    'total price must be a number': 'Total price must be a valid number.',
    'quantity per unit must be a number': 'Quantity per unit must be a valid number.',
    '"items[0].description" is not allowed to be empty': 'Item description is required.',
    '"items[0].unit_price" is not allowed to be empty': 'Unit price is required.',
    '"items[0].quantity" is not allowed to be empty': 'Quantity is required.',
    '"items[0].total_price" is not allowed to be empty': 'Total price is required.',
    '"items[0].line_number" is not allowed to be empty': 'Item line number is required.',
    '"purchase_order.po_number" is not allowed to be empty': 'PO Number is required.',
    '"purchase_order.supplier_id" is not allowed to be empty': 'Supplier is required.',
    '"purchase_order.status" is not allowed to be empty': 'Status is required.',
    '"purchase_order.po_number" is required': 'PO Number is required.',
    '"purchase_order.supplier_id" is required': 'Supplier is required.',
    '"purchase_order.status" is required': 'Status is required.',
    'items': 'At least one item must include a description.',

    // Inventory item validations (backend Joi messages)
    'Tenant ID must be a valid UUID': 'Tenant is invalid.',
    'Tenant ID is required': 'Tenant is required.',
    'Serial number must be less than 100 characters': 'Serial number must be under 100 characters.',
    'Serial number is required': 'Serial number is required.',
    'Part name must be less than 150 characters': 'Part name must be under 150 characters.',
    'Part name is required': 'Part name is required.',
    'Machine ID must be a valid UUID': 'Machine ID must be a valid selection.',
    'Entry date must be in ISO format (YYYY-MM-DD)': 'Entry date must be in format YYYY-MM-DD.',
    'Purchase quantity must be a number': 'Purchase quantity must be a number.',
    'Purchase quantity must be an integer': 'Purchase quantity must be an integer.',
    'Purchase quantity must be at least 1': 'Purchase quantity must be at least 1.',
    'Purchase quantity is required': 'Purchase quantity is required.',
    'Quantity sold must be a number': 'Quantity sold must be a number.',
    'Quantity sold must be an integer': 'Quantity sold must be an integer.',
    'Quantity sold cannot be negative': 'Quantity sold cannot be negative.',
    'Current quantity must be a number': 'Current quantity must be a number.',
    'Current quantity must be an integer': 'Current quantity must be an integer.',
    'Current quantity cannot be negative': 'Current quantity cannot be negative.',
    'Rate per unit must be a number': 'Rate per unit must be a number.',
    'Rate per unit cannot have more than 2 decimal places': 'Rate per unit cannot have more than 2 decimal places.',
    'Rate per unit cannot be negative': 'Rate per unit cannot be negative.',
    'Rate per unit is required': 'Rate per unit is required.',
    'Total amount must be a number': 'Total amount must be a number.',
    'Total amount cannot have more than 2 decimal places': 'Total amount cannot have more than 2 decimal places.',
    'Total amount cannot be negative': 'Total amount cannot be negative.',
    'PO item ID must be a valid UUID': 'PO item ID must be a valid selection.',
    'Remark must be a string': 'Remark must be a text value.',

    // Inventory empty/required patterns
    '"serial_number" is not allowed to be empty': 'Serial number is required.',
    '"part_name" is not allowed to be empty': 'Part name is required.',
    '"purchase_quantity" is not allowed to be empty': 'Purchase quantity is required.',
    '"rate_per_unit" is not allowed to be empty': 'Rate per unit is required.',
    '"tenant_id" is not allowed to be empty': 'Tenant is required.',

    // Catch-all for items array
    'At least one item must be provided': 'At least one item must be provided.',

    // Supplier validations (backend Joi messages)
    'Supplier name cannot be empty': 'Supplier name cannot be empty.',
    'Supplier name must be at least 1 character': 'Supplier name must be at least 1 character.',
    'Supplier name must be less than 150 characters': 'Supplier name must be less than 150 characters.',
    'Supplier name is required': 'Supplier name is required.',
    'Contact person name must be less than 100 characters': 'Contact person name must be less than 100 characters.',
    'Phone number must be 10-15 digits (string or number)': 'Phone number must be 10-15 digits.',
    'Phone number is required': 'Phone number is required.',
    'Email must be a valid email': 'Email must be a valid email address.',
    'Email is required': 'Email is required.',
    '"email" must be a string': 'Email is required.',
    'Address must be at least 5 characters': 'Address must be at least 5 characters.',
    'Address must be less than 255 characters': 'Address must be less than 255 characters.',
    'Address is required': 'Address is required.',
    'Verification status must be a boolean value': 'Verification status must be a boolean value.',
    'Verification date must be in ISO format': 'Verification date must be in ISO format (YYYY-MM-DD).',

    // Supplier empty/required patterns
    '"supplier_name" is not allowed to be empty': 'Supplier name is required.',
    '"phone_number" is not allowed to be empty': 'Phone number is required.',
    '"email" is not allowed to be empty': 'Email is required.',
    '"address" is not allowed to be empty': 'Address is required.',

    // Supplier duplicate / DB constraint patterns
    'suppliers_supplier_name_key': 'Supplier name already exists. Please use a unique supplier name.',
    'suppliers_phone_number_key': 'Phone number already exists. Please use a different phone number.',
    'suppliers_email_key': 'Email address already exists. Please use a different email address.',

    // Machine validations (backend Joi messages)
    'Machine name cannot be empty': 'Machine name is required.',
    'Machine name must be at least 1 character': 'Machine name must be at least 1 character.',
    'Machine name must be less than 150 characters': 'Machine name must be less than 150 characters.',
    'Machine name is required': 'Machine name is required.',
    'Machine number cannot be empty': 'Machine number is required.',
    'Machine number must be at least 1 character': 'Machine number must be at least 1 character.',
    'Machine number must be less than 100 characters': 'Machine number must be less than 100 characters.',
    'Machine number is required': 'Machine number is required.',

    // Machine empty/required patterns
    '"machine_name" is not allowed to be empty': 'Machine name is required.',
    '"machine_number" is not allowed to be empty': 'Machine number is required.',
    '"machine_name" must be a string': 'Machine name is required.',
    '"machine_number" must be a string': 'Machine number is required.',
  };

  // Check if message contains any of the keys (case-insensitive partial match)
  for (const [key, friendlyMessage] of Object.entries(validationMappings)) {
    if (message.toLowerCase().includes(key.toLowerCase())) {
      return friendlyMessage;
    }
  }

  return message;
};

// Extract 1-based item index from path like items.0.unit_price
const extractItemIndex = (fieldPath) => {
  const parts = fieldPath.split('.');
  const idxPart = parts.find(p => /^\d+$/.test(p));
  const idxNum = idxPart ? Number(idxPart) + 1 : 1;
  return idxNum;
};

// Humanize field path like purchase_order.po_number or items.0.unit_price
const buildReadableField = (fieldPath) => {
  const parts = fieldPath.split('.');
  return parts
    .map((part, idx) => {
      if (!isNaN(Number(part))) {
        return `Item ${Number(part) + 1}`;
      }
      if (idx === 0 && part === 'items') return 'Item';
      if (idx === 0 && part === 'purchase_order') return 'Purchase Order';
      return capitalize(part.replace(/_/g, ' '));
    })
    .join(' ');
};

const buildRequiredFieldMessage = (fieldPath) => {
  const human = buildReadableField(fieldPath);
  if (fieldPath.startsWith('items.')) {
    const idx = extractItemIndex(fieldPath);
    const label = human.replace(/^Item \\d+\\s*/, '').trim();
    return `${label ? `${label} ` : 'Field '}is required for item ${idx}.`;
  }
  return `${human} is required.`;
};

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : str);

// Show toast notification for errors
export const showErrorToast = (message, duration = 5000) => {
  // Dispatch custom event for toast notifications
  window.dispatchEvent(new CustomEvent('showToast', {
    detail: {
      message,
      type: 'error',
      duration
    }
  }));
};

// Show success toast notification
export const showSuccessToast = (message, duration = 3000) => {
  window.dispatchEvent(new CustomEvent('showToast', {
    detail: {
      message,
      type: 'success',
      duration
    }
  }));
};

// Show warning toast notification
export const showWarningToast = (message, duration = 4000) => {
  window.dispatchEvent(new CustomEvent('showToast', {
    detail: {
      message,
      type: 'warning',
      duration
    }
  }));
};

// Show info toast notification
export const showInfoToast = (message, duration = 4000) => {
  window.dispatchEvent(new CustomEvent('showToast', {
    detail: {
      message,
      type: 'info',
      duration
    }
  }));
};

// Show error dialog popup
export const showErrorDialog = (message, title = 'Error', buttonText = 'OK') => {
  window.dispatchEvent(new CustomEvent('showErrorDialog', {
    detail: {
      title,
      message,
      buttonText
    }
  }));
};

// Export the database error mapper for direct use if needed
export { mapDatabaseErrorMessage };